import { ChangeDetectionStrategy, Component, DOCUMENT, inject, input, OnInit, signal } from '@angular/core';
import { ListLayout3 } from '@components/list/components/list-layout-3/model';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { Image } from '@shared/models';
import { getId } from '@shared/utils';

interface ImageWithId extends Image {
  id: string;
}

@Component({
  selector: 'app-list-layout-3',
  imports: [TitleComponent, ImageComponent],
  templateUrl: './list-layout-3.component.html',
  styleUrl: './list-layout-3.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListLayout3Component implements OnInit {
  readonly data = input.required<ListLayout3>();
  title = signal<Title>(null!);
  imagesDisplayed = signal<ImageWithId[]>([]);
  imagesHidden = signal<ImageWithId[]>([]);
  private document = inject<Document>(DOCUMENT);
  private contextService = inject(ContextService);
  readonly #logsService = inject(LogsService);

  ngOnInit(): void {
    this.title.set({
      level: 'H2',
      size: {
        mobile: 'H2',
        desktop: 'H1',
      },
      color: 'BLACK',
      align: {
        mobile: 'CENTER',
        desktop: 'CENTER',
      },
      text: this.data().title ?? '',
    });
    const imagesWithId: ImageWithId[] = this.data().items.map((item) => ({ ...item, id: getId() }));
    imagesWithId.sort(() => 0.5 - Math.random());
    this.imagesDisplayed.set(imagesWithId.slice(0, 15));
    this.imagesHidden.set(imagesWithId.slice(15, imagesWithId.length));
    // Do not start the interval when no swap is possible (15 images or fewer: imagesHidden is empty)
    if (this.contextService.isBrowser() && this.imagesHidden().length > 0) {
      setInterval(() => {
        this.swapImage();
      }, 2000);
    }
  }

  private swapImage() {
    const imageToHideIndex = Math.floor(Math.random() * this.imagesDisplayed().length);
    const imageToShowIndex = Math.floor(Math.random() * this.imagesHidden().length);

    const imageToHide = this.imagesDisplayed()[imageToHideIndex];
    const imageToShow = this.imagesHidden()[imageToShowIndex];

    // Vérifiez si les objets existent et que leurs `id` sont valides
    if (!imageToHide?.id || !imageToShow?.id) {
      this.#logsService.debug('ListLayout3Component', 'Image swap skipped: missing image IDs.');
      return;
    }

    const imgElement = this.document.getElementById(`img-${imageToHide.id}`);
    if (imgElement) {
      imgElement.classList.toggle('item-hide');
      setTimeout(() => {
        const updatedDisplayed = [...this.imagesDisplayed()];
        const updatedHidden = [...this.imagesHidden()];

        updatedDisplayed[imageToHideIndex] = { ...imageToShow };
        updatedHidden[imageToShowIndex] = { ...imageToHide };

        this.imagesDisplayed.set(updatedDisplayed);
        this.imagesHidden.set(updatedHidden);
        imgElement.classList.toggle('item-hide');
      }, 500);
    } else {
      this.#logsService.warn('ListLayout3Component', `Image element with id img-${imageToHide.id} not found.`);
    }
  }
}
